import { queryOptions, useQuery } from '@tanstack/react-query';
import type { ViewDTO } from '@tempo-io/roadmap-dto';
import { RoadmapApi } from 'src/lib/api/roadmapping/roadmap';
import { ViewApi } from 'src/lib/api/roadmapping/view';
import { viewQueryKeys } from '@/lib/query-keys/view';

export type TemporaryLoopViewType = Omit<ViewDTO, 'roadmapId'> & { loopId: number };

const getTemporaryMockedVisualizationViews = async (
  loopId: number,
  organizationId: string,
): Promise<TemporaryLoopViewType[]> => {
  const allRoadmaps = await RoadmapApi.fetchRoadmaps(); // This is just to simulate some loading time, can be removed when real API is used
  const firstRoadmap = allRoadmaps[0];
  if (!firstRoadmap) {
    return [];
  }
  const views = await ViewApi.fetchViews(firstRoadmap.roadmapId); // This is just to simulate some loading time, can be removed when real API is used
  const firstView = views[0];
  if (firstView) {
    return [
      {
        ...firstView,
        loopId,
      },
    ];
  }

  return [
    {
      viewId: 1,
      name: 'Default (mocked) Loop View',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      loopId,
      locked: false,
      lockedAt: null,
      lockedBy: null,
      organizationId,
    },
  ];
};

/**
 * Query options factory for fetching all views.
 */
export const getVisualizationViewsQueryOptions = ({
  org,
  loopId,
}: {
  org: string;
  loopId: number;
}) =>
  queryOptions({
    queryKey: viewQueryKeys.visualizationsView.list(loopId),
    queryFn: () => getTemporaryMockedVisualizationViews(loopId, org), // Replace with actual API call when loop views are supported
    enabled: !!loopId,
  });

export const getVisualizationViewQueryOptions = ({
  org,
  loopId,
}: {
  org: string;
  loopId: number;
}) =>
  queryOptions({
    queryKey: viewQueryKeys.visualizationsView.details(loopId),
    queryFn: async () => {
      const mockedViews = await getTemporaryMockedVisualizationViews(loopId, org);
      return mockedViews.length > 0 ? mockedViews[0] : null;
    }, // Replace with actual API call when loop views are supported
  });

export const useVisualizationViews = (loopId: number) =>
  useQuery(getVisualizationViewsQueryOptions({ org: '', loopId }));

export const useVisualizationView = (loopId: number) => {
  return useQuery({
    ...getVisualizationViewQueryOptions({ org: '', loopId }),
    enabled: !!loopId,
  });
};
